export type TriedgeParityRouteStatus =
  | 'loading'
  | 'loaded'
  | 'error'
  | 'auth_redirect'
  | 'unmapped'
  | 'unsupported';

export type TriedgeParityQueryState = {
  key: string;
  operation?: string;
  status: 'uninitialized' | 'pending' | 'fulfilled' | 'rejected';
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  hasData: boolean;
  error?: string | null;
};

export type TriedgeParityRouteReadiness = {
  caseId: string;
  status: TriedgeParityRouteStatus;
  pathname: string;
  reason?: string | null;
  queries: TriedgeParityQueryState[];
  inspectedAt: string;
};

export type TriedgeParityBridgeImplementation = {
  primeRoute?: (caseId: string) => Promise<void> | void;
  getRouteReadiness: (
    caseId: string,
  ) => Promise<TriedgeParityRouteReadiness> | TriedgeParityRouteReadiness;
};

export type TriedgeParityBridge = {
  register: (implementation: TriedgeParityBridgeImplementation | null) => void;
  primeRoute: (caseId: string) => Promise<void>;
  getRouteReadiness: (caseId: string) => Promise<TriedgeParityRouteReadiness>;
};

const createUnsupportedReadiness = (
  caseId: string,
  reason: string,
): TriedgeParityRouteReadiness => ({
  caseId,
  status: 'unsupported',
  pathname: typeof window !== 'undefined' ? window.location.pathname : '',
  reason,
  queries: [],
  inspectedAt: new Date().toISOString(),
});

export const createTriedgeParityBridge = (): TriedgeParityBridge => {
  let implementation: TriedgeParityBridgeImplementation | null = null;

  return {
    register(nextImplementation) {
      implementation = nextImplementation;
    },
    async primeRoute(caseId) {
      if (!implementation?.primeRoute) {
        return;
      }
      await implementation.primeRoute(caseId);
    },
    async getRouteReadiness(caseId) {
      if (!implementation?.getRouteReadiness) {
        return createUnsupportedReadiness(caseId, 'No parity bridge implementation is registered.');
      }
      try {
        return await implementation.getRouteReadiness(caseId);
      } catch (error) {
        return {
          ...createUnsupportedReadiness(
            caseId,
            error instanceof Error ? error.message : String(error ?? 'Unknown parity bridge error.'),
          ),
          status: 'error',
        };
      }
    },
  };
};
